// Step 12-3 · C (owner card Step 12 V4, section C) — the ONE Export button of the IFRS statements page: a drop-down with
// Excel, Word and PDF. PDF = the Publication PDF (its own route, watermark, SHA-256 and 409 tie-check gate, moved here
// unchanged from IFRS.jsx); Excel / Word = a background job (kind fs_export, POST /fs/{id}/export) whose file is saved
// from GET /jobs/{id}/file. The old "Export PDF" (P&L + balance sheet only) is no longer offered.
// Before any export the page asks the server which notes do not tie (card A0.4) and shows them; the export goes ahead.
// After any export it calls onExported(kind): IFRS.jsx re-reads the publication trail and the engagement (D13: the
// "Regenerate needed" banner goes without a reload). On an Arabic screen the menu says the export is in English for now (C5).
import React, { useState } from "react";
import { ChevronDown, Download, FileSpreadsheet, FileText, FileType, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { fmtNumber } from "@/components/Amount";
import { askConfirm } from "@/lib/appDialog";
import { waitForJob } from "@/lib/waitJob";
import { downloadJobFile } from "@/lib/jobFile";
import { jobErrorText } from "@/lib/jobErrorText";

export const TIE_CHECK_FAILED = "FS_EXPORT_TIE_CHECK_FAILED";

/** The "These notes do not tie: …" text (card A0.4), or "" when every note ties. */
export function untiedText(t, rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";
  const shown = list.slice(0, 6).map((r) => `• ${t("s12c.untiedRow")
    .replace("{n}", r.printed_no ?? r.note_key ?? "")
    .replace("{title}", r.title || r.label || "")
    .replace("{diff}", fmtNumber(Number(r.difference ?? 0)))}`);
  if (list.length > 6) shown.push(`• ${t("s12c.untiedMore").replace("{n}", list.length - 6)}`);
  return `${t("s12c.untiedTitle")}\n${shown.join("\n")}\n${t("s12c.untiedGoAhead")}`;
}

export default function FsExportMenu({ engagement, user, disabled, title, onExported, onBlocked }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const engId = engagement?.id;
  const kindName = (k) => t(`s12c.kind.${k}`);

  const warnUntied = async () => {
    try {
      const r = await api.get(`/fs/${engId}/export/preflight`, { _silent: true });
      const d = r.data || {};
      // Step 12-1's own sentence (EN / AR) when the server has it; else built from the rows
      const own = lang === "ar" ? d.message_ar : d.message;
      const txt = (d.untied || []).length ? (own ? `${own}\n${t("s12c.untiedGoAhead")}` : untiedText(t, d.untied)) : "";
      if (txt) toast.warning(txt, { duration: 12000 });
    } catch { /* the warning is a courtesy — never stops the export */ }
  };

  // ---- PDF = the Publication PDF (unchanged behaviour: 409 gate, Platform Admin override, download, trail)
  const exportPdf = async (opts = {}) => {
    const override = !!opts.override;
    const token = localStorage.getItem("af_token");
    const headers = { Authorization: `Bearer ${token}` };
    if (override) headers["X-Publication-Override"] = "true";
    const resp = await fetch(`${API_URL}/fs/${engId}/publication-pdf`, { headers });
    if (resp.status === 409) {
      const body = await resp.json().catch(() => ({}));
      const detail = body?.detail || body || {};
      const failed = detail.failed_checks || [];
      if (user?.role === "Platform Admin") {
        const summary = failed.slice(0, 4).map((c) => `• ${c.label} (residual ${fmtNumber(Number(c.residual))})`).join("\n")
          + (failed.length > 4 ? `\n• +${failed.length - 4} more…` : "");
        const ok = await askConfirm(`${t("ifrs.tieCheckFailedHeader")}\n\n`
          + `${t("ifrs.tieCheckSummary").replace("{passed}", detail.passed).replace("{total}", detail.total)}\n\n`
          + `${t("ifrs.tieCheckFailedList")}\n${summary}\n\n${t("ifrs.tieCheckOverridePrompt")}`);
        if (!ok) return false;
        return exportPdf({ override: true });
      }
      toast.error(t("ifrs.publicationBlocked").replace("{n}", failed.length));
      if (onBlocked) onBlocked();
      return false;
    }
    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || t("ifrs.publicationExportFailed"));
    }
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${engagement.client_name}_FY${engagement.financial_year}_Financial_Statements.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
    const wm = resp.headers.get("X-FS-Publication-Watermark") || "";
    if (wm === "TIE_CHECK_FAILED") toast.warning(t("ifrs.publicationExportedOverride"));
    else toast.success(t("ifrs.publicationExportedOk"));
    return true;
  };

  // ---- Excel / Word: a background job (switch OFF: the web process; ON: the worker)
  const exportFile = async (fmt, opts = {}) => {
    const r = await api.post(`/fs/${engId}/export`, { format: fmt, override: !!opts.override });
    const job = await waitForJob(r.data.job_id);
    if (job?.status === "done") {
      await downloadJobFile(r.data.job_id, `${engagement.client_name}_FY${engagement.financial_year}_Financial_Statements.${fmt}`);
      toast.success(t("s12c.done").replace("{kind}", kindName(fmt)));
      return true;
    }
    if (job?.error_code === TIE_CHECK_FAILED) {
      if (user?.role === "Platform Admin" && !opts.override) {
        const ok = await askConfirm(`${t("ifrs.tieCheckFailedHeader")}\n\n${jobErrorText(job, lang, t)}\n\n${t("ifrs.tieCheckOverridePrompt")}`);
        if (!ok) return false;
        return exportFile(fmt, { override: true });
      }
      toast.error(t("s12c.blocked").replace("{n}", ((job.error || "").split("Failing:")[1] || "").split(";").filter(Boolean).length || 1));
      if (onBlocked) onBlocked();
      return false;
    }
    throw new Error(jobErrorText(job, lang, t, t("s12c.failed").replace("{kind}", kindName(fmt))));
  };

  const run = async (kind) => {
    setOpen(false);
    if (disabled) { if (title) toast.error(title); return; }
    setBusy(kind);
    try {
      await warnUntied();
      const ok = kind === "pdf" ? await exportPdf() : await exportFile(kind);
      if (ok && onExported) await onExported(kind);
    } catch (e) {
      toast.error(toErrMsg(e, kind === "pdf" ? t("ifrs.publicationExportFailed") : t("s12c.failed").replace("{kind}", kindName(kind))));
    } finally {
      setBusy("");
    }
  };

  const item = (kind, Icon, label) => (
    <li>
      <button type="button" onClick={() => run(kind)} data-testid={`fs-export-${kind}`}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
        <Icon className="h-4 w-4 flex-none text-slate-500" /> {label}
      </button>
    </li>
  );

  return (
    <div className="relative" data-testid="fs-export">
      <Button onClick={() => setOpen((v) => !v)} disabled={!!busy || disabled} title={title}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fs-export-btn"
              aria-haspopup="menu" aria-expanded={open}>
        {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Download className="me-1 h-4 w-4" />}
        {busy ? t("s12c.building").replace("{kind}", kindName(busy)) : t("s12c.exportBtn")}
        <ChevronDown className="ms-1 h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute end-0 top-full z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-900"
             data-testid="fs-export-menu">
          <ul>
            {item("xlsx", FileSpreadsheet, t("s12c.excel"))}
            {item("docx", FileType, t("s12c.word"))}
            {item("pdf", FileText, t("s12c.pdf"))}
          </ul>
          {lang === "ar" && (
            <p className="border-t border-slate-100 px-2 pt-1.5 pb-1 text-xs text-muted-foreground dark:border-slate-800"
               data-testid="fs-export-english-only">{t("s12c.englishOnly")}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** The kind badge of a publication-trail row: "PDF · DRAFT" (a row without kind is a Publication PDF), "Excel", "Word". */
export function PubKind({ row }) {
  const { t } = useI18n();
  const kind = row?.kind || "pdf";
  const label = t(`s12c.kind.${kind}`) + (kind === "pdf" && row?.watermark ? ` · ${row.watermark}` : "");
  return (
    <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold dark:bg-slate-800"
          data-testid={`ifrs-pub-kind-${kind}`}>{label}</span>
  );
}
